import { useEffect, useState, createContext, useContext } from "react";
import * as React from "react";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  // createHttpLink,
  NormalizedCacheObject,
} from "@apollo/client";
import { cache, userDetailsVar } from "./cache";
import { setContext } from "@apollo/client/link/context";
import {
  GET_USER_DETAILS,
  LOGIN_USER,
} from "../graphql/operations/userOperations";
import { createUploadLink } from "apollo-upload-client";
import { FullSizeIconLoading } from "../components/loading/FullSizeIconLoading";
import { RestLink } from "apollo-link-rest";

interface IAuth {
  isSignedIn: boolean;
  signIn: (email: string, password: string, remember?: boolean) => Promise<any>;
  client: undefined | ApolloClient<NormalizedCacheObject>;
  signOut: () => void;
  authToken: string | null;
}

const authContext = createContext<IAuth>({} as IAuth);

function useProvideAuth() {
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("@illuma_at")
  );

  const isSignedIn = !!authToken;
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();

  useEffect(() => {
    async function init() {
      await setClient(createApolloClient());
    }

    init().catch((err) => err);
  }, [authToken]);

  useEffect(() => {
    retrieveToken(setAuthToken);
  }, []);

  useEffect(() => {
    async function initUser() {
      await client
        ?.query({ query: GET_USER_DETAILS, fetchPolicy: "network-only" })
        .then(({ data }) => {
          userDetailsVar(data.me);
        });
    }
    initUser().catch((err) => err);
  }, [client]);

  const createApolloClient: () => ApolloClient<NormalizedCacheObject> = () => {
    const authLink = setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          authorization: authToken ? `JWT ${authToken}` : "",
        },
      };
    });

    // const httpLink = createHttpLink({
    //   uri: process.env.REACT_APP_API_URI,
    //   credentials: "include",
    // });

    // We use an upload link instead of http link because we require file uploading
    const uploadLink = createUploadLink({
      uri: process.env.REACT_APP_API_URI,
      credentials: "include",
    }) as unknown as ApolloLink;

    const restLinkHitl = new RestLink({
      uri: process.env.REACT_APP_REST_API_URI_HITL,
    });
    // "https://hitl.illumaland.com/" prod endpoint
    // "https://dev-hitl.illuma-tech.com/" dev endpoint

    const restLinkIllumaDb = new RestLink({
      uri: process.env.REACT_APP_FAST_API_URI,
    });
    const graphqlLinkIllumaDb = new HttpLink({
      uri: `${process.env.REACT_APP_FAST_API_URI}graphql`,
    });

    return new ApolloClient({
      // link: authLink.concat(uploadLink),
      link: ApolloLink.split(
        (operation) => operation.getContext().clientName === "hitlEndpoint",
        restLinkHitl, // Routes operations to the HITL REST endpoint
        ApolloLink.split(
          (operation) =>
            operation.getContext().clientName === "illumaDbEndpoint",
          ApolloLink.split(
            (operation) => operation.getContext().type === "graphql", // Add GraphQL type context
            graphqlLinkIllumaDb, // Routes GraphQL operations to Illuma DB endpoint
            restLinkIllumaDb // Routes REST operations to Illuma DB endpoint
          ),
          authLink.concat(uploadLink) // Default: file upload or other operations
        )
      ),
      cache,
      connectToDevTools: true, //process.env.NODE_ENV === "development",
      resolvers: {},
    });
  };

  const signIn = async (email: string, password: string, remember = false) => {
    if (!client) return;

    const { data, errors } = await client.mutate({
      mutation: LOGIN_USER,
      variables: { email, password },
    });

    if (remember) {
      const currentDate = new Date();
      const expiryDate = currentDate.setDate(currentDate.getDate() + 30); // token expires after 30 days
      localStorage.setItem("@illuma_at", data.userLogin.token);
      localStorage.setItem("@illuma_at_exp_date", expiryDate.toString());
    } else {
      // session storage date not required as it clears on tab close
      sessionStorage.setItem("@illuma_at", data.userLogin.token);
    }

    if (data?.userLogin?.token) {
      setAuthToken(data.userLogin.token);
      return Promise.resolve();
    } else {
      return Promise.reject(errors);
    }
  };

  const signOut = () => {
    setAuthToken("");
    userDetailsVar(null);
    client?.clearStore();
    localStorage.clear();
    sessionStorage.clear();
  };

  return {
    isSignedIn,
    client,
    authToken,
    signIn,
    signOut,
  };
}

export const AuthProvider: React.FC = ({ children }) => {
  const auth = useProvideAuth();

  if (!auth.client) {
    return <FullSizeIconLoading />;
  }

  return (
    <authContext.Provider value={auth}>
      <ApolloProvider client={auth.client}>{children}</ApolloProvider>
    </authContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(authContext);
};

export const retrieveToken = (setAuthToken: any) => {
  const localAuthToken = localStorage.getItem("@illuma_at");
  const localAuthExpiry = Number(localStorage.getItem("@illuma_at_exp_date"));

  const authTokenValid =
    localAuthToken && !isNaN(localAuthExpiry) && localAuthExpiry > Date.now();

  if (authTokenValid) {
    setAuthToken(localAuthToken);
  } else {
    localStorage.removeItem("@illuma_at");
    localStorage.removeItem("@illuma_at_exp_date");
  }
};
